import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { playAchievementSound } from '@/lib/sounds';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { handleSupabaseError } from '@/lib/errors/handleSupabaseError';
import { log } from '@/lib/logger';
import type { Achievement, AchievementNotifications, Rarity } from './types';

/**
 * Mutation/write-side: concede uma conquista ao usuário e dispara notificação.
 * RPC `grant_achievement` previne self-grant; pontos via `award_user_points`.
 */
export function useGrantAchievement() {
  const queryClient = useQueryClient();

  const createInAppNotification = useCallback(
    async (userId: string, achievement: { name: string; id: string; points_reward: number }) => {
      try {
        await supabase.from('in_app_notifications').insert({
          user_id: userId,
          title: '🏆 Conquista Desbloqueada!',
          message: `Você desbloqueou "${achievement.name}" e ganhou ${achievement.points_reward} pontos!`,
          type: 'achievement',
          reference_id: achievement.id,
        });
        queryClient.invalidateQueries({ queryKey: ['in-app-notifications'] });
      } catch (error) {
        handleSupabaseError(error, 'Erro ao criar notificação.', { silent: true, context: { hook: 'useGrantAchievement.notify' } });
      }
    },
    [queryClient],
  );

  const grantAchievement = useCallback(
    async (userId: string, achievement: Achievement) => {
      const moduleType = achievement.category === 'enem' ? 'enem' : 'concursos';

      const { error: rpcError } = await (supabase.rpc as any)('grant_achievement', {
        p_achievement_id: achievement.id,
        p_module_type: moduleType,
      });
      if (rpcError) {
        log.error({ err: { message: rpcError.message, code: rpcError.code }, achievementId: achievement.id }, 'achievement.grant.failed');
        return;
      }

      await (supabase.rpc as any)('award_user_points', {
        p_amount: achievement.points_reward,
        p_module_type: moduleType,
      });

      const { data: prefs } = await supabase
        .from('notification_preferences')
        .select('achievement_notifications')
        .eq('user_id', userId)
        .maybeSingle();

      const defaultPrefs: AchievementNotifications = {
        common: true, rare: true, epic: true, legendary: true,
      };
      const raw = prefs?.achievement_notifications as Record<string, boolean> | null;
      const notifyPrefs: AchievementNotifications = raw
        ? {
            common: raw.common ?? true,
            rare: raw.rare ?? true,
            epic: raw.epic ?? true,
            legendary: raw.legendary ?? true,
          }
        : defaultPrefs;
      const rarity = (achievement.rarity || 'common') as Rarity;

      const toastConfig: Record<Rarity, { icon: string; duration: number }> = {
        common: { icon: '🏆', duration: 3000 },
        rare: { icon: '💎', duration: 4000 },
        epic: { icon: '🔮', duration: 5000 },
        legendary: { icon: '👑', duration: 7000 },
      };

      if (notifyPrefs[rarity]) {
        const config = toastConfig[rarity];
        playAchievementSound(rarity);
        toast.success(
          `${config.icon} Conquista Desbloqueada: ${achievement.name}! +${achievement.points_reward} pontos`,
          { duration: config.duration },
        );
      }

      await createInAppNotification(userId, achievement);
    },
    [createInAppNotification],
  );

  return { grantAchievement };
}
